class TrGrid extends HTMLElement {
	selectors = {
		button: '[data-tr-js-grid-button]',
	};

	attributes = {
		block: 'data-tr-js-grid-block',
		breakpoint: 'data-tr-js-grid-breakpoint',
		columns: 'data-tr-js-grid-columns',
		defaultColumns: 'data-tr-js-grid-default-columns',
		defaultMobileColumns: 'data-tr-js-grid-default-mobile-columns',
	};

	stateClasses = {
		isActive: 'is-active',
	};

	connectedCallback() {
		this.buttonElements = [...this.querySelectorAll(this.selectors.button)];

		this.init();
		this.bindEvents();

		document.addEventListener('shopify:section:load', this.onSectionLoad);
		document.addEventListener('shopify:block:select', this.onBlockSelect);
	}

	disconnectedCallback() {
		document.removeEventListener('shopify:section:load', this.onSectionLoad);
		document.removeEventListener('shopify:block:select', this.onBlockSelect);

		this.destroy();
	}

	init() {
		const initialColumns = this.getInitialColumns();
		const initialMobileColumns = this.getInitialMobileColumns();

		if (this.isValidColumns(initialColumns)) {
			this.setColumns(initialColumns, 'desktop');
		}

		if (this.isValidColumns(initialMobileColumns)) {
			this.setColumns(initialMobileColumns, 'mobile');
		}
	}

	bindEvents() {
		this.buttonElements.forEach(buttonElement => {
			buttonElement.addEventListener('click', this.onButtonClick);
		});
	}

	onSectionLoad = event => {
		if (
			!this.closest(`#${event.detail.sectionId}`) &&
			!this.closest(`#shopify-section-${event.detail.sectionId}`)
		)
			return;

		this.destroy();

		this.buttonElements = [...this.querySelectorAll(this.selectors.button)];

		this.init();
		this.bindEvents();
	};

	onBlockSelect = event => {
		const selectedButtonElement = this.buttonElements.find(
			buttonElement =>
				buttonElement.getAttribute(this.attributes.block) === event.detail.blockId,
		);

		if (!selectedButtonElement) return;

		this.setColumns(
			this.getButtonColumns(selectedButtonElement),
			this.getButtonBreakpoint(selectedButtonElement),
		);
	};

	onButtonClick = event => {
		const columns = this.getButtonColumns(event.currentTarget);
		const breakpoint = this.getButtonBreakpoint(event.currentTarget);

		if (!this.isValidColumns(columns)) return;

		this.setColumns(columns, breakpoint);
	};

	setColumns(columns, breakpoint = 'desktop') {
		if (breakpoint === 'mobile') {
			this.style.setProperty('--trGridMobileColumns', columns);
		} else {
			this.style.setProperty('--trGridColumns', columns);
		}

		this.buttonElements.forEach(buttonElement => {
			const buttonBreakpoint = this.getButtonBreakpoint(buttonElement);

			if (buttonBreakpoint !== breakpoint) return;

			const isActive = this.getButtonColumns(buttonElement) === columns;

			buttonElement.classList.toggle(this.stateClasses.isActive, isActive);
			buttonElement.setAttribute('aria-pressed', isActive ? 'true' : 'false');
		});
	}

	getInitialColumns() {
		const defaultColumns = Number(
			this.getAttribute(this.attributes.defaultColumns),
		);

		if (this.isValidColumns(defaultColumns)) return defaultColumns;

		return this.getButtonColumns(this.buttonElements[0]);
	}

	getInitialMobileColumns() {
		const defaultMobileColumns = Number(
			this.getAttribute(this.attributes.defaultMobileColumns),
		);

		if (this.isValidColumns(defaultMobileColumns)) return defaultMobileColumns;

		const mobileButtonElement = this.buttonElements.find(
			buttonElement => this.getButtonBreakpoint(buttonElement) === 'mobile',
		);

		return this.getButtonColumns(mobileButtonElement);
	}

	getButtonColumns(buttonElement) {
		if (!buttonElement) return null;

		return Number(buttonElement.getAttribute(this.attributes.columns));
	}

	getButtonBreakpoint(buttonElement) {
		if (!buttonElement) return 'desktop';

		return buttonElement.getAttribute(this.attributes.breakpoint) || 'desktop';
	}

	isValidColumns(columns) {
		return Number.isInteger(columns) && columns > 0;
	}

	destroy() {
		this.buttonElements.forEach(buttonElement => {
			buttonElement.removeEventListener('click', this.onButtonClick);
			buttonElement.classList.remove(this.stateClasses.isActive);
			buttonElement.setAttribute('aria-pressed', 'false');
		});

		this.style.removeProperty('--trGridColumns');
		this.style.removeProperty('--trGridMobileColumns');
	}
}

if (!customElements.get('tr-grid')) {
	customElements.define('tr-grid', TrGrid);
}
